import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChatMessage from '../Onboarding/chat/ChatMessage';
import AssistantMessage from './AssistantMessage';
import { latestArtifactMessage } from './chatArtifactDisplayModel';

const BOTTOM_THRESHOLD_PX = 80;

const THREAD_SHELL_SX = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const SCROLL_CONTAINER_SX = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  px: { xs: 1.5, sm: 3 },
  pt: { xs: 2, sm: 3 },
  pb: { xs: 3, sm: 3 },
};

const MESSAGE_LANE_SX = {
  width: '100%',
  maxWidth: '920px',
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const USER_BUBBLE_SX = {
  alignSelf: 'flex-end',
  bgcolor: 'primary.main',
  color: '#FFFFFF',
  px: 2,
  py: 1.5,
  borderRadius: '18px 18px 4px 18px',
  width: 'fit-content',
  maxWidth: 'min(760px, 86%)',
  mb: 1,
};

const USER_BUBBLE_MODERN_SX = {
  alignSelf: 'flex-end',
  bgcolor: 'rgba(74, 44, 245, 0.08)',
  color: 'text.primary',
  border: '1px solid',
  borderColor: 'rgba(74, 44, 245, 0.14)',
  px: 1.75,
  py: 1.25,
  borderRadius: '16px 16px 4px 16px',
  width: 'fit-content',
  maxWidth: 'min(720px, 92%)',
  mb: 1.25,
};

const MESSAGE_LANE_MODERN_SX = {
  width: '100%',
  maxWidth: '760px',
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const JUMP_ROW_SX = {
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center',
  px: 2,
  py: 1,
};

function isNearBottom(container) {
  const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
  return remaining <= BOTTOM_THRESHOLD_PX;
}

function prefersReducedMotion() {
  return Boolean(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  );
}

export default function MessageThread({
  messages = [],
  conversationId = 'new-chat',
  busy = false,
  streamingId = null,
  exportingId = null,
  onExportCompanies,
  artifactActionInFlight = null,
  onRunArtifactAction,
  onSendSuggestion,
  onSelectClarificationOption,
  suggestionsDisabled = false,
  variant = 'legacy',
}) {
  const scrollContainerRef = useRef(null);
  const contentRef = useRef(null);
  const shouldFollowRef = useRef(true);
  const previousScrollTopRef = useRef(0);
  const previousMessagesRef = useRef([]);
  const previousConversationRef = useRef();
  const hasRenderedRef = useRef(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const setFollowing = useCallback((following) => {
    shouldFollowRef.current = following;
    setShowJumpToLatest((current) => current === !following ? current : !following);
  }, []);

  const scrollToLatest = useCallback((behavior = 'auto') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const resolvedBehavior = prefersReducedMotion() ? 'instant' : behavior;
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: container.scrollHeight, behavior: resolvedBehavior });
      // `auto` updates synchronously and test/browser implementations of
      // `smooth` may begin immediately. Capture the real position so later
      // upward movement can be distinguished from the smooth downward path.
      previousScrollTopRef.current = container.scrollTop;
      return;
    }
    container.scrollTop = container.scrollHeight;
    previousScrollTopRef.current = container.scrollTop;
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const currentScrollTop = container.scrollTop;
    const movedUp = currentScrollTop < previousScrollTopRef.current;
    previousScrollTopRef.current = currentScrollTop;

    if (isNearBottom(container)) {
      setFollowing(true);
    } else if (movedUp) {
      setFollowing(false);
    }
  }, [setFollowing]);

  const handleJumpToLatest = useCallback(() => {
    setFollowing(true);
    scrollToLatest('smooth');
  }, [scrollToLatest, setFollowing]);

  useLayoutEffect(() => {
    const firstRender = !hasRenderedRef.current;
    const conversationChanged = previousConversationRef.current !== conversationId;
    const previousMessages = previousMessagesRef.current;
    const appendedMessages = messages.length > previousMessages.length
      ? messages.slice(previousMessages.length)
      : [];
    const userSubmitted = appendedMessages.some((message) => message.role === 'user');

    if (firstRender || conversationChanged || userSubmitted) {
      setFollowing(true);
    }
    if (shouldFollowRef.current) {
      scrollToLatest(firstRender || conversationChanged ? 'auto' : 'smooth');
    }

    hasRenderedRef.current = true;
    previousConversationRef.current = conversationId;
    previousMessagesRef.current = messages;
  }, [busy, conversationId, messages, scrollToLatest, setFollowing]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const content = contentRef.current;
    const followResize = () => {
      if (shouldFollowRef.current) scrollToLatest('auto');
    };
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(followResize)
      : null;

    if (container) resizeObserver?.observe(container);
    if (content) resizeObserver?.observe(content);
    window.addEventListener('resize', followResize);
    window.visualViewport?.addEventListener('resize', followResize);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', followResize);
      window.visualViewport?.removeEventListener('resize', followResize);
    };
  }, [scrollToLatest]);

  const latestListId = latestArtifactMessage(messages)?.id;
  const isModern = variant === 'modern';
  const messageLaneSx = isModern ? MESSAGE_LANE_MODERN_SX : MESSAGE_LANE_SX;
  const userBubbleSx = isModern ? USER_BUBBLE_MODERN_SX : USER_BUBBLE_SX;

  // BE #874: only the latest answer's options are clickable; an older question's reply would be stale.
  const latestAssistantId = [...messages].reverse().find((message) => message.role === 'assistant')?.id;

  return (
    <Box sx={THREAD_SHELL_SX} data-testid="message-thread" data-thread-variant={variant}>
      <Box
        ref={scrollContainerRef}
        sx={SCROLL_CONTAINER_SX}
        data-testid="message-scroll-container"
        aria-busy={busy}
        onScroll={handleScroll}
      >
        <Box ref={contentRef} sx={messageLaneSx} data-testid="message-lane">
          {messages.map((msg) => {
            if (msg.role === 'user') {
              if (isModern) {
                return (
                  <ChatMessage key={msg.id} role="user" variant="chat">
                    {msg.content}
                  </ChatMessage>
                );
              }
              return (
                <Box key={msg.id} sx={userBubbleSx}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.content}
                  </Typography>
                </Box>
              );
            }

            const artifactActionInFlightKey =
              msg.id === exportingId
                ? 'export_csv'
                : artifactActionInFlight?.[msg.id]
                  ? artifactActionInFlight[msg.id].key
                  : null;

            return (
              <AssistantMessage
                key={msg.id}
                content={msg.content}
                notice={msg.notice}
                companies={msg.companies}
                artifactAnswer={msg.artifactAnswer}
                artifactAnswerActions={msg.artifactAnswerActions}
                suggestions={msg.suggestions}
                clarificationOptions={msg.id === latestAssistantId ? msg.clarificationOptions : []}
                onSelectClarificationOption={onSelectClarificationOption}
                artifactDisplay={msg.artifactDisplay}
                isLatestList={msg.id === latestListId}
                artifactActionInFlightKey={artifactActionInFlightKey}
                isStreaming={msg.id === streamingId}
                refinementSummary={msg.refinementSummary}
                progressLabel={msg.progressLabel}
                ownerProgress={msg.ownerProgress}
                ownerProgressStatus={msg.ownerProgressStatus}
                ownerProgressRefreshError={msg.ownerProgressRefreshError}
                showExport={Boolean(msg.artifactId)}
                exportDisabled={msg.companies.length === 0}
                isExporting={msg.id === exportingId}
                exportError={msg.exportError}
                exportWarning={msg.exportWarning}
                onExportCompanies={() => onExportCompanies?.(msg.id)}
                onRunArtifactAnswerAction={(actionKey) => onRunArtifactAction?.(msg.id, actionKey)}
                artifactActionNotice={msg.artifactActionNotice}
                artifactActionNoticeSeverity={msg.artifactActionNoticeSeverity}
                artifactActionError={msg.artifactActionError}
                workspace={msg.artifactWorkspace}
                onRunArtifactAction={(actionKey) => onRunArtifactAction?.(msg.id, actionKey)}
                onSendSuggestion={(suggestion) => onSendSuggestion?.(msg.id, suggestion)}
                suggestionsDisabled={suggestionsDisabled}
                visualVariant={isModern ? 'modern' : 'legacy'}
              />
            );
          })}
        </Box>
      </Box>
      {showJumpToLatest ? (
        <Box sx={JUMP_ROW_SX}>
          <Button
            type="button"
            size="small"
            variant="outlined"
            startIcon={<KeyboardArrowDownIcon />}
            onClick={handleJumpToLatest}
          >
            Jump to latest
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
